import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { FixScore } from "../components/FixScore.js";
import { MatchRow, dayLabel } from "../components/MatchRow.js";
// The confirm / dispute / cancel block is shared with the event results screen, which needs
// exactly the same controls over exactly the same rows.
import { Confirmation, confirmableBy } from "../components/MatchTools.js";
import { Button, Notice, Selector } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import {
  MATCH_PAGE,
  awaitsConfirmationFrom,
  confirmMatch,
  disputeMatch,
  fetchMatches,
  fetchMyMatches,
  voidMatch,
  type ConfirmResult,
  type MatchFilters,
} from "../lib/matches.js";
import { fetchMember } from "../lib/members.js";
import {
  matchRates,
  type Kind,
  type MatchCategory,
  type MatchContext,
  type MatchStatus,
} from "../lib/types.js";

/**
 * The match log, and the queue that feeds it.
 *
 * Two things on one screen because they are two halves of the same object. The queue is
 * the half of the rating loop people forget to do — a match nobody confirms never reaches
 * the engine, so it stays pinned at the top and is always about you. Below it is every
 * match the community has played, because a league you can only see your own results in is
 * a private notebook.
 *
 * Filters live in the URL. A board someone is looking at is a thing they will want to send
 * to somebody, and back out of.
 */
export function Matches() {
  const { member } = useAuth();
  const queryClient = useQueryClient();
  // `delta: null` is a confirmation that moved nothing on purpose — a mixed match. It is
  // not the same as no result at all, which is why it is a null rather than an absence.
  const [result, setResult] = useState<{ matchId: string; delta: number | null } | null>(null);
  /** Which match is open for correction, if any. One at a time. */
  const [fixing, setFixing] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [params, setParams] = useSearchParams();

  const player = params.get("player");
  const kind = (params.get("kind") ?? "all") as Kind | "all";
  const category = (params.get("category") ?? "all") as MatchCategory | "all";
  const status = (params.get("status") ?? "all") as MatchStatus | "all";
  const context = (params.get("context") ?? "all") as MatchContext | "all";

  const filters: MatchFilters = {
    ...(player ? { memberId: player } : {}),
    ...(kind === "all" ? {} : { kind }),
    ...(category === "all" ? {} : { category }),
    ...(status === "all" ? {} : { status }),
    ...(context === "all" ? {} : { context }),
  };

  // One param at a time, keeping the rest. `replace` so a run of filter changes does not
  // fill the back button with states nobody chose to visit.
  const setParam = (name: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(name);
    else next.set(name, value);
    setParams(next, { replace: true });
  };

  const { data: mine = [] } = useQuery({
    queryKey: ["my-matches", member?.id],
    queryFn: () => fetchMyMatches(member!.id),
    enabled: Boolean(member),
  });

  const feed = useInfiniteQuery({
    queryKey: ["match-feed", filters],
    queryFn: ({ pageParam }) => fetchMatches(filters, { offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.more ? pages.length * MATCH_PAGE : undefined),
  });

  // The person the feed is narrowed to, for the label. Only asked for when it is somebody
  // else — you already know your own name.
  const { data: filteredTo } = useQuery({
    queryKey: ["member", player],
    queryFn: () => fetchMember(player!),
    enabled: Boolean(player) && player !== member?.id,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["my-matches"] });
    void queryClient.invalidateQueries({ queryKey: ["match-feed"] });
  };

  const confirm = useMutation({
    mutationFn: confirmMatch,
    onSuccess: (confirmed: ConfirmResult, matchId) => {
      const change = confirmed.changes.find((c) => c.member_id === member?.id);
      if (!confirmed.rated) setResult({ matchId, delta: null });
      else if (change) setResult({ matchId, delta: change.delta });
      refresh();
    },
  });

  // Cancelling is an organiser's action, not a participant's. void_match enforces that in
  // the database too, so the button's absence is a courtesy rather than the control.
  const canCancel = hasRole(member, "organiser");

  const cancel = useMutation({
    mutationFn: (input: { matchId: string; reason: string }) =>
      voidMatch(input.matchId, input.reason),
    onSuccess: refresh,
  });

  const dispute = useMutation({
    mutationFn: (input: { matchId: string; reason: string }) =>
      disputeMatch(input.matchId, input.reason),
    onSuccess: refresh,
  });

  const awaiting = mine.filter((match) => member && awaitsConfirmationFrom(match, member.id));
  const matches = feed.data?.pages.flatMap((page) => page.matches) ?? [];
  // "Whose" is said by the heading, so it isn't counted on the Filter button.
  const filterCount = Object.keys(filters).filter((key) => key !== "memberId").length;
  const filtered = Object.keys(filters).length > 0;

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <PageHeader eyebrow="The match log" title="Matches" />

      {confirm.error ? <Notice tone="attention">{confirm.error.message}</Notice> : null}
      {dispute.error ? <Notice tone="attention">{dispute.error.message}</Notice> : null}
      {cancel.error ? <Notice tone="attention">{cancel.error.message}</Notice> : null}

      <section className="mb-10">
        <h2
          className="font-mono mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em]"
          style={{
            color: awaiting.length > 0 ? "var(--color-attention)" : "var(--color-ink-muted)",
          }}
        >
          {awaiting.length > 0 ? (
            <span
              aria-hidden
              className="size-2.5"
              style={{ background: "var(--color-attention-fill)" }}
            />
          ) : null}
          Waiting on you · {awaiting.length}
        </h2>

        {awaiting.length === 0 ? (
          <Notice>Nothing to confirm. Matches your opponents log land here.</Notice>
        ) : (
          <div className="flex flex-col gap-3">
            {awaiting.map((match) => (
              <MatchRow key={match.id} match={match} memberId={member?.id} framed>
                <Confirmation
                  busy={confirm.isPending || dispute.isPending || cancel.isPending}
                  canCancel={canCancel}
                  // Confirmed is not the same as rated any more: a mixed match reaches
                  // 'confirmed' having moved nobody, so cancelling it needs no recompute.
                  rated={match.status === "confirmed" && matchRates(match.category)}
                  onConfirm={() => confirm.mutate(match.id)}
                  onDispute={(reason) => dispute.mutate({ matchId: match.id, reason })}
                  onCancel={(reason) => cancel.mutate({ matchId: match.id, reason })}
                />
              </MatchRow>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="font-mono mb-3 flex flex-wrap items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
          <span>
            {player
              ? player === member?.id
                ? "Your matches"
                : `${filteredTo?.displayName ?? "One player"}’s matches`
              : "Everyone"}
          </span>
          <span className="flex items-center gap-3">
            {filtered ? (
              <button
                type="button"
                className="underline"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
              >
                Clear
              </button>
            ) : null}
            {/* Five selects took two rows above the log on a phone. They fold behind one
                button that says how many are on, so a filtered log never looks unfiltered. */}
            <button
              type="button"
              aria-expanded={showFilters}
              onClick={() => setShowFilters((open) => !open)}
              className="flex min-h-[44px] items-center gap-2 border-2 px-3 text-ink"
              style={{
                borderColor: "var(--color-line)",
                background: filterCount > 0 ? "var(--color-line)" : "transparent",
                color: filterCount > 0 ? "var(--color-ground)" : "var(--color-ink)",
              }}
            >
              Filter{filterCount > 0 ? ` · ${filterCount}` : ""}
            </button>
          </span>
        </div>

        {showFilters ? (
          <div className="mb-5 flex flex-wrap gap-2">
            <Selector
              label="Whose matches"
              value={player ? (player === member?.id ? "mine" : "player") : "all"}
              onChange={(value) =>
                setParam("player", value === "mine" ? (member?.id ?? null) : null)
              }
              options={[
                { value: "all", label: "Everyone" },
                { value: "mine", label: "Mine" },
                // Only reachable from a profile, and shown so the selector is not sitting on
                // a value it cannot display.
                ...(player && player !== member?.id
                  ? [{ value: "player", label: filteredTo?.displayName ?? "One player" }]
                  : []),
              ]}
            />
            <Selector
              label="Discipline"
              value={kind}
              onChange={(value) => setParam("kind", value === "all" ? null : value)}
              options={[
                { value: "all", label: "Both" },
                { value: "doubles", label: "Doubles" },
                { value: "singles", label: "Singles" },
              ]}
            />
            <Selector
              label="Match type"
              value={category}
              onChange={(value) => setParam("category", value === "all" ? null : value)}
              options={[
                { value: "all", label: "Any type" },
                { value: "mens", label: "Men’s" },
                { value: "womens", label: "Women’s" },
                { value: "mixed", label: "Mixed" },
              ]}
            />
            {/* Tournament matches sit in the same log as everything else — one table, one
              rating engine — so the only thing worth adding is a way to look at one kind. */}
            <Selector
              label="Where"
              value={context}
              onChange={(value) => setParam("context", value === "all" ? null : value)}
              options={[
                { value: "all", label: "Anywhere" },
                { value: "casual", label: "Casual" },
                { value: "event", label: "Tournament" },
              ]}
            />
            <Selector
              label="Status"
              value={status}
              onChange={(value) => setParam("status", value === "all" ? null : value)}
              options={[
                { value: "all", label: "Any status" },
                { value: "confirmed", label: "Rated" },
                { value: "pending", label: "Awaiting" },
                { value: "disputed", label: "Disputed" },
                { value: "void", label: "Cancelled" },
              ]}
            />
          </div>
        ) : null}

        {feed.isPending ? <Notice>Loading matches…</Notice> : null}
        {feed.error ? (
          <Notice tone="attention">Couldn&rsquo;t load the match log. Try again.</Notice>
        ) : null}

        {feed.isSuccess && matches.length === 0 ? (
          <Notice>
            {filtered
              ? "No matches fit those filters."
              : "No matches yet. Log one and the board starts filling."}
          </Notice>
        ) : null}

        {matches.map((match, index) => (
          <Fragment key={match.id}>
            {/* A day heading on a tramline wherever the date changes: the log reads as the
              pages of a scorebook rather than one long list. */}
            {index === 0 || dayLabel(matches[index - 1]!.playedAt) !== dayLabel(match.playedAt) ? (
              <div className="mt-6">
                <h3 className="font-mono mb-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
                  {dayLabel(match.playedAt)}
                </h3>
                <div className="tramline" aria-hidden />
              </div>
            ) : null}
            <MatchRow
              match={match}
              memberId={member?.id}
              showWhen={false}
              action={
                canCancel && match.status !== "void" && fixing !== match.id ? (
                  <button
                    type="button"
                    onClick={() => setFixing(match.id)}
                    className="-my-3 inline-flex min-h-[44px] items-center underline"
                  >
                    Fix score
                  </button>
                ) : null
              }
            >
              {/* Gains glow, losses are stated plainly — the same rule the lockup follows.
                Rendering a loss in the live colour was the first thing a real confirmed
                match made obvious. */}
              {result?.matchId === match.id ? (
                result.delta === null ? (
                  // A mixed match confirms with nothing to report. Saying so beats the
                  // silence, which reads as a confirmation that did not work.
                  <p className="mt-2 text-sm text-ink-muted">
                    Confirmed. Mixed matches don’t affect ratings.
                  </p>
                ) : (
                  <p
                    className="font-mono mt-2 text-sm"
                    style={{ color: result.delta >= 0 ? "var(--color-up)" : "var(--color-down)" }}
                  >
                    Confirmed. Your rating moved{" "}
                    <span className={result.delta >= 0 ? "highlight px-1" : ""}>
                      {result.delta >= 0 ? "▲" : "▼"}{" "}
                      {(Math.abs(result.delta) / 200).toFixed(3).replace(/^0/, "")}
                    </span>
                  </p>
                )
              ) : null}

              {/* A match anybody can still confirm is confirmable from here too, not only from
                the queue above. The queue is about *your* matches, and an admin's job is
                often the opposite one: scores entered for players who have gone home, sitting
                pending until somebody with the authority to say so signs them off. */}
              {confirmableBy(match, member) ? (
                <Confirmation
                  busy={confirm.isPending || dispute.isPending || cancel.isPending}
                  canCancel={canCancel}
                  rated={match.status === "confirmed" && matchRates(match.category)}
                  onConfirm={() => confirm.mutate(match.id)}
                  onDispute={(reason) => dispute.mutate({ matchId: match.id, reason })}
                  onCancel={(reason) => cancel.mutate({ matchId: match.id, reason })}
                />
              ) : null}

              {/* Correcting a score is the organiser's, and it lives here rather than in the
                queue above: that queue is about agreeing to a score, not rewriting one. A
                cancelled match has nothing to correct — it already says it never happened.
                The way in is the small "Fix score" at the end of the row's status line. */}
              {fixing === match.id ? (
                <FixScore
                  match={match}
                  onCancel={() => setFixing(null)}
                  onDone={() => {
                    setFixing(null);
                    refresh();
                  }}
                />
              ) : null}
            </MatchRow>
          </Fragment>
        ))}

        {feed.hasNextPage ? (
          <div className="mt-6">
            <Button onClick={() => void feed.fetchNextPage()} disabled={feed.isFetchingNextPage}>
              {feed.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        ) : null}

        {player && player !== member?.id ? (
          <p className="micro mt-6">
            <Link className="underline" to={`/player/${player}`}>
              Go to {filteredTo?.displayName ?? "their"} profile
            </Link>
          </p>
        ) : null}
      </section>
    </main>
  );
}
